// Feed media sizing (Mantis 0013106). Every image or video fills the post's
// width and keeps its own shape, clamped between 16:9 (widest) and 4:5
// (tallest). Anything wider is letterboxed. Anything taller is shown at 4:5:
// a video (9:16 phone clip) is centre-cropped to fill the full width, as
// Meta's feeds do; an image keeps a blurred copy of itself on the sides so
// nothing in a screenshot or chart is cut off.
//
// Desktop: the frame keeps the full post width with its height capped at
// min(600px, 70vh). Nothing is cropped; media narrower than the frame sits
// in the middle with a blurred copy of itself filling the sides.
const WIDEST = 16 / 9;
const TALLEST = 4 / 5;

// Neutral fill behind letterboxed media instead of black.
export const FEED_MEDIA_FRAME_CLASS =
  "relative isolate w-full overflow-hidden rounded-lg bg-[#f0f2f5]";

// Ratio stored with the post on upload; for older posts without one, the
// ratio measured once the media has loaded.
export const feedMediaRatio = (media, measured) =>
  media?.width > 0 && media?.height > 0
    ? media.width / media.height
    : measured || null;

// Until an older post's media loads, reserve a 16:9 box. On desktop the
// width stays 100% and max-height caps the box (16:9 780x439, 1:1 and 4:5
// 780x600).
export const feedMediaFrameStyle = (ratio, desktop = false) => {
  const shown = ratio ? Math.min(Math.max(ratio, TALLEST), WIDEST) : WIDEST;
  return desktop
    ? { aspectRatio: shown, width: "100%", maxHeight: "min(600px, 70vh)" }
    : { aspectRatio: shown };
};

// Object-fit for a feed video: on mobile, centre-crop when taller than 4:5;
// on desktop, always whole.
export const feedVideoFit = (ratio, desktop = false) =>
  !desktop && ratio && ratio < TALLEST ? "object-cover" : "object-contain";

// Blurred fill on the sides of media narrower than its frame: taller than
// 4:5 on mobile, narrower than 16:9 on desktop. Sits behind the media
// (negative z inside the frame's isolated stacking context).
export const FeedMediaBackdrop = ({ ratio, src, desktop = false }) =>
  ratio && ratio < (desktop ? WIDEST : TALLEST) && src ? (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      className="absolute inset-0 -z-10 w-full h-full object-cover scale-110 blur-xl opacity-70"
    />
  ) : null;

// Pixel size of a picked file, read before upload so it can be stored with
// the post. Resolves to {} when the browser cannot decode the file.
export const readMediaSize = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    let settled = false;
    const done = (width, height) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      resolve(width > 0 && height > 0 ? { width, height } : {});
    };
    setTimeout(() => done(0, 0), 5000);

    if (/^video\//.test(file.type) || /\.(mp4|mov|avi|wmv)$/i.test(file.name)) {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => done(video.videoWidth, video.videoHeight);
      video.onerror = () => done(0, 0);
      video.src = url;
    } else {
      const img = new Image();
      img.onload = () => done(img.naturalWidth, img.naturalHeight);
      img.onerror = () => done(0, 0);
      img.src = url;
    }
  });
